import { generatePath } from 'react-router-dom';

import { FormInstance } from '@components/form/create-form';
import { SelectOption } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { routePaths } from 'ui/authenticated/navigation';

export type EmailAddressOptions = {
  options: SelectOption[];
  defaultId?: string;
  isLoading: boolean;
};

type SendFromEmailSelectProps<T> = {
  Form: FormInstance<T>;
  emailAddressOptions: EmailAddressOptions;
  id?: string;
  defaultValue?: string;
  isRequired?: boolean;
  label?: string;
};

export const SendFromEmailSelect = <T,>({
  Form,
  emailAddressOptions,
  id = 'sendFromEmailId',
  defaultValue,
  isRequired = true,
  label,
}: SendFromEmailSelectProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const resolvedDefaultValue =
    defaultValue ??
    emailAddressOptions.defaultId ??
    emailAddressOptions.options[0]?.value ??
    'system';

  return (
    <Form.Select
      id={id as Extract<keyof T, string>}
      label={label ?? t('sendFrom')}
      addOption={{
        label: t('addAddress'),
        href: generatePath(routePaths.settingsEmailDomain, {}),
      }}
      options={emailAddressOptions.options}
      validators={isRequired ? ['required'] : []}
      defaultValue={resolvedDefaultValue}
      parseSingleValue={(option: SelectOption) => (
        <Stack orientation="horizontal" space={1} align="center">
          <Text font="regular16" text={option.label} color="textPrimary" ellipsis />
          <Text font="regular16" text={`(${option.helpText})`} color="textHelper" ellipsis />
        </Stack>
      )}
    />
  );
};

const locale = {
  en: {
    sendFrom: 'Send from',
    addAddress: 'Add email address',
  },
};
